import { getAllData } from 'apis/customApiHandler';
import View from 'components/agreed-cpkm/view';
import useDebounce from 'hooks/useDebounce';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { Button, Input } from 'styled';
import { TH } from 'styled/table';
import checkPermission from 'utils/checkPermission';
import permissions from 'utils/permissionConstants';
import ReactSelect from "react-select";
import { BiCloudDownload } from "react-icons/bi";
import exportExcel from 'utils/exportExcel';
import toast from 'react-hot-toast';

const lockOptions = [
  { value: "", label: "All" },
  { value: "locked", label: "Locked" },
  { value: "unlocked", label: "Unlocked" },
];

const fleetTypeOptions = [
  { value: "", label: "All" },
  { value: true, label: "Mobility" },
  { value: false, label: "Fleet" },
];

const reactSelectStyles = {
  control: (base, state) => ({
    ...base,
    height: "40px",
    minHeight: "40px",
    fontSize: "0.875rem",
    padding: "0px",
    borderRadius: "6px",
    borderColor: state.isFocused ? "#22c55e" : "#d1d5db",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#22c55e",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 6px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "40px",
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "2px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "2px",
  }),
  multiValue: (base) => ({
    ...base,
    fontSize: "0.75rem",
    borderRadius: "4px",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.875rem",
    backgroundColor: state.isSelected
      ? "#dcfce7"
      : state.isFocused
      ? "#f0fdf4"
      : "white",
    color: "#111827",
  }),
};

const AgreedCPKM = () => {
  // searchInput is what the user is typing; appliedSearch is what the query runs
  // on. It still follows the input after the debounce, but the Search button (and
  // Enter) applies it straight away instead of making the user wait.
  const [searchInput, setSearchInput] = useState('');
  const currentUser = useSelector((state) => state.user.currentUser);
  const [lockStatusFilter, setLockStatusFilter] = useState("");
  const [fleetTypeFilter, setFleetTypeFilter] = useState("");
  const [downloading, setDownloading] = useState(false);
  const debouncedValue = useDebounce(searchInput, 500);
  const [appliedSearch, setAppliedSearch] = useState('');

  useEffect(() => {
    setAppliedSearch(debouncedValue);
  }, [debouncedValue]);

  const applySearch = () => setAppliedSearch(searchInput);

  const buildFilter = () => {
    const filter = {};
    if (appliedSearch) filter.searchQuery = appliedSearch;

    if (lockStatusFilter === "locked") filter.canCreateOrder = false;
    else if (lockStatusFilter === "unlocked") filter.canCreateOrder = true;

    if (fleetTypeFilter !== "") filter.isMobility = fleetTypeFilter;

    return filter;
  };

  const { data: fleets } = useQuery(
    ['customer-fleet', appliedSearch, lockStatusFilter, fleetTypeFilter],
    () => getAllData('/customer-fleet', buildFilter())
  );

  const showActionsColumn = checkPermission(
    [permissions.manage_masters, permissions.view_fleet_master_actions],
    currentUser
  );

  const handleDownload = () => {
    if (!fleets?.data?.length) {
      toast.error('No records available to download');
      return;
    }
    setDownloading(true);
    // Deferred so the "Downloading..." state paints before the (synchronous) excel generation.
    setTimeout(() => {
      exportExcel(
        fleets.data.map((fleet, idx) => ({
          "S. No.": idx + 1,
          "Fleet Code": fleet.fleetCode,
          "Fleet Name": fleet.fleetName,
          "Agreed CPKM": fleet.agreedCPKM,
          ...(showActionsColumn && {
            "New Order Request": fleet.canCreateOrder === false ? "Locked" : "Unlocked",
          }),
        })),
        "fleet-master"
      );
      setDownloading(false);
    }, 0);
  };


  return (
    <div>
      <div className="m-10">
        <div className="mt-8 flex justify-start gap-6 items-center flex-wrap lg:flex-nowrap">

          <div className="relative flex-1 min-w-[16rem]">
            <Input
              type="search"
              placeholder="Search Here"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applySearch();
              }}
            />
          </div>
          <Button
            type="button"
            className="h-10 !py-0 !px-8 whitespace-nowrap"
            bgPrimary
            onClick={applySearch}
          >
            Search
          </Button>
          <Button
            className="h-10 !py-0 !px-8 disabled:bg-gray-400 whitespace-nowrap"
            bgPrimary
            disabled={downloading || !fleets?.data?.length}
            onClick={handleDownload}
          >
            <div className="flex gap-1 items-center">
              <BiCloudDownload className="w-5 h-5" />
              {downloading ? "Downloading..." : "Download"}
            </div>
          </Button>
        </div>

        <div className="mt-4 flex gap-6 items-start flex-wrap">
          <div className="w-60">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fleet Type
            </label>
            <ReactSelect
              value={fleetTypeOptions.find((option) => option.value === fleetTypeFilter)}
              onChange={(selected) => setFleetTypeFilter(selected?.value)}
              options={fleetTypeOptions}
              className="mb-2"
              styles={reactSelectStyles}
            />
          </div>

          {currentUser?.userRole?.name === "SUPER_ADMIN" && (
            <div className="w-60">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lock Status
              </label>
              <ReactSelect
                value={lockOptions.find((option) => option.value === lockStatusFilter)}
                onChange={(selected) => setLockStatusFilter(selected?.value)}
                options={lockOptions}
                isClearable
                className="mb-2"
                styles={reactSelectStyles}
              />
            </div>
          )}
        </div>


      </div>
      <div className="flex flex-col m-2 lg:m-10">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <TH scope="col">S.N.</TH>
                    <TH scope="col">Fleet Code</TH>
                    <TH scope="col">Fleet Name</TH>
                    <TH scope="col">Agreed CPKM</TH>
                    {showActionsColumn && (
                      <TH scope="col">New Order Request</TH>
                    )}

                    <TH scope="col">Actions</TH>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {fleets?.data?.length ? (
                    fleets.data.map((fleet, index) => (
                      <View
                        fleet={fleet}
                        key={fleet._id}
                        index={index}
                      />
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={showActionsColumn ? 6 : 5}
                        className="text-center p-6 text-sm text-gray-500"
                      >
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreedCPKM;